/**
 * Data-bound field widgets (sitebuilder/SPEC.md §1.1a), backed by GET /api/user/sites/{id}/picker/*:
 * - ObjectSearch: one searchable combobox over site pages / products / posts / collections / CMS items, optionally
 *   accepting a typed external URL (link fields, menus, redirects).
 * - LinkTargetInput: link {href, label, target, ref}: ref only for internal targets.
 * - SourceInput: {kind, from, value, ids, sort, limit, exclude_current}.
 * - RefInput: {kind, id}.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Checkbox, Input, Segmented, Select, Slider, Switch, Tooltip, Typography } from 'antd';
import { CaretDownOutlined, CaretRightOutlined, DeleteOutlined, HolderOutlined, LinkOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../../shared/i18n';
import { site as siteApi, QUIET } from '../api';

const { Text } = Typography;

export const REF_KINDS = ['page', 'product', 'post', 'collection', 'item'];
const SOURCE_KINDS = ['products', 'posts', 'cms'];
const ITEM_KIND = { products: 'product', posts: 'post', cms: 'item' };
const FROMS = {
    products: ['page', 'all', 'collection', 'tag', 'vendor', 'manual'],
    posts: ['page', 'all', 'category', 'tag', 'author', 'series', 'featured', 'manual'],
    cms: ['type', 'manual'],
};
const SORTS = {
    products: ['default', 'newest', 'price-asc', 'price-desc', 'best', 'title'],
    posts: ['default', 'oldest', 'title'],
    cms: ['default', 'newest', 'title'],
};
/** source kind + from → picker/values kind */
const VALUE_KIND = {
    'products.collection': 'collection', 'products.tag': 'tag', 'products.vendor': 'vendor',
    'posts.category': 'category', 'posts.tag': 'post_tag', 'posts.author': 'author', 'posts.series': 'series', 'cms.type': 'cms_type',
};
const reURL = /^(https?:\/\/|mailto:|tel:|\/|#)/i;

const useKindLabels = () => {
    const { t } = useTranslation();
    return useMemo(() => ({
        page: t('Pages'), product: t('Products'), post: t('Posts'), collection: t('Collections'), item: t('CMS items'),
        products: t('Products'), posts: t('Posts'), cms: t('CMS'),
        from: {
            page: t('Automatic'), all: t('All'), collection: t('Collection'), tag: t('Tag'), vendor: t('Vendor'), manual: t('Pick manually'),
            category: t('Category'), author: t('Author'), series: t('Series'), featured: t('Featured'), type: t('CMS collection'),
        },
        sort: {
            default: t('Default'), newest: t('Newest'), oldest: t('Oldest'), 'price-asc': t('Price: low to high'), 'price-desc': t('Price: high to low'),
            best: t('Best selling'), title: t('Title A–Z'),
        },
    }), [t]);
};

// ---------- titles of saved references (batched, cached per site) ----------
const cache = new Map(); // `${siteId}|${kind}|${id}` → item | false (gone)
const pending = new Map(); // `${siteId}|${kind}` → Set(ids)
const waiters = new Set();
let flushTimer = null;
const ck = (siteId, kind, id) => `${siteId}|${kind}|${id}`;
const flush = () => {
    flushTimer = null;
    const jobs = [...pending.entries()];
    pending.clear();
    Promise.all(jobs.map(async ([key, set]) => {
        const [siteId, kind] = key.split('|');
        const ids = [...set];
        try {
            const r = await siteApi(siteId).get('/picker/search', { kind, ids: ids.join(',') }, QUIET);
            (r?.items || []).forEach((it) => cache.set(ck(siteId, kind, it.id), it));
        } catch (_) { /* leave unknown */ }
        ids.forEach((id) => { if (!cache.has(ck(siteId, kind, id))) cache.set(ck(siteId, kind, id), false); });
    })).then(() => waiters.forEach((w) => w()));
};
const want = (siteId, kind, id) => {
    if (!siteId || !kind || !id || cache.has(ck(siteId, kind, id))) return;
    const key = `${siteId}|${kind}`;
    if (!pending.has(key)) pending.set(key, new Set());
    pending.get(key).add(id);
    if (!flushTimer) flushTimer = setTimeout(flush, 40);
};
/** [{kind, id}] → { `${kind}|${id}`: item | false | undefined (loading) } */
const useRefItems = (siteId, refs) => {
    const [, bump] = useState(0);
    useEffect(() => {
        const w = () => bump((n) => n + 1);
        waiters.add(w);
        return () => waiters.delete(w);
    }, []);
    const key = refs.map((r) => `${r.kind}|${r.id}`).join(',');
    useEffect(() => { refs.forEach((r) => want(siteId, r.kind, r.id)); }, [siteId, key]); // eslint-disable-line react-hooks/exhaustive-deps
    const out = {};
    refs.forEach((r) => { out[`${r.kind}|${r.id}`] = cache.get(ck(siteId, r.kind, r.id)); });
    return out;
};
const remember = (siteId, it) => cache.set(ck(siteId, it.kind, it.id), it);

const Thumb = ({ src }) => (src
    ? <img src={src} alt="" style={{ width: 22, height: 22, objectFit: 'cover', borderRadius: 4, flex: 'none' }} />
    : <span style={{ width: 22, height: 22, borderRadius: 4, background: '#f0f1f4', flex: 'none' }} />);

const ItemRow = ({ it }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
        <Thumb src={it.image} />
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>{it.title || it.id}</span>
        {it.status && !['published', 'active'].includes(it.status) && <Text type="secondary" style={{ fontSize: 11 }}>{it.status}</Text>}
        {it.subtitle && <Text type="secondary" style={{ fontSize: 11, maxWidth: 90 }} ellipsis>{it.subtitle}</Text>}
    </div>
);

/**
 * Searchable pick of one site object. value: {kind, id} | null; href: the external URL shown when there is no value.
 * onPick(item) for an object, onUrl(text) for a typed URL (allowUrl), onClear() (allowClear).
 */
export const ObjectSearch = ({
    siteId, kinds = REF_KINDS, value, href, onPick, onUrl, onClear, allowUrl = false, allowClear = false, needUrl = false,
    exclude, placeholder, size = 'small', style,
}) => {
    const { t } = useTranslation();
    const L = useKindLabels();
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState('');
    const [results, setResults] = useState({});
    const [loading, setLoading] = useState(false);
    const seq = useRef(0);
    const kindsKey = kinds.join(',');
    const refs = useMemo(() => (value?.kind && value?.id ? [value] : []), [value?.kind, value?.id]); // eslint-disable-line react-hooks/exhaustive-deps
    const cur = useRefItems(siteId, refs);

    useEffect(() => {
        if (!open || !siteId) return undefined;
        const n = ++seq.current;
        setLoading(true);
        const h = setTimeout(async () => {
            const per = kinds.length > 1 ? 6 : 20;
            const out = {};
            await Promise.all(kinds.map(async (kind) => {
                try {
                    const r = await siteApi(siteId).get('/picker/search', { kind, q, per_page: per }, QUIET);
                    out[kind] = r?.items || [];
                    out[kind].forEach((it) => remember(siteId, it));
                } catch (_) { out[kind] = []; }
            }));
            if (n === seq.current) { setResults(out); setLoading(false); }
        }, q ? 220 : 0);
        return () => clearTimeout(h);
    }, [open, q, siteId, kindsKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const ex = new Set(exclude || []);
    const groups = kinds.map((kind) => ({
        label: L[kind],
        options: (results[kind] || []).filter((it) => !ex.has(`${kind}:${it.id}`) && (!needUrl || it.url)).map((it) => ({
            value: `${kind}:${it.id}`, label: <ItemRow it={it} />, item: it,
        })),
    })).filter((g) => g.options.length);
    const typed = q.trim();
    if (allowUrl && typed && (reURL.test(typed) || /^[\w-]+(\.[\w-]+)+/.test(typed))) {
        const url = reURL.test(typed) ? typed : `https://${typed}`;
        const g = { label: t('External link'), options: [{ value: `url:${url}`, label: <span><LinkOutlined /> {url}</span>, url }] };
        if (/^[/#]/.test(url)) groups.push(g); else groups.unshift(g); // a typed path: site objects first
    }
    let curKey;
    let curLabel = null;
    if (value?.kind && value?.id) {
        curKey = `${value.kind}:${value.id}`;
        const it = cur[`${value.kind}|${value.id}`];
        curLabel = it ? <ItemRow it={it} /> : <span>{it === false ? <Text type="danger">{t('Not found')}: </Text> : null}{value.id}</span>;
    } else if (href) {
        curKey = `url:${href}`;
        curLabel = <span><LinkOutlined /> {href}</span>;
    }
    const all = groups.flatMap((g) => g.options);
    // the saved value shows through labelRender, so it need not be one of the search results
    const commitTyped = () => {
        if (allowUrl && typed && reURL.test(typed)) onUrl?.(typed);
    };
    return (
        <Select size={size} style={{ width: '100%', ...style }} showSearch filterOption={false} value={curKey} open={open}
            onDropdownVisibleChange={(o) => { setOpen(o); if (!o) setQ(''); }}
            searchValue={q} onSearch={setQ} loading={loading} allowClear={allowClear}
            placeholder={placeholder || (allowUrl ? t('Search pages, products… or paste a URL') : t('Search'))}
            notFoundContent={loading ? null : <Text type="secondary" style={{ fontSize: 12 }}>{t('No results')}</Text>}
            options={groups} labelRender={(o) => (o.value === curKey ? curLabel : o.label)}
            onClear={() => onClear?.()}
            onBlur={commitTyped}
            onInputKeyDown={(e) => { if (e.key === 'Enter' && typed && !all.length) { e.preventDefault(); commitTyped(); setOpen(false); } }}
            onSelect={(key, opt) => {
                setQ('');
                if (opt.url) onUrl?.(opt.url);
                else if (opt.item) onPick?.(opt.item);
            }} />
    );
};

/** link value {href, label, target, rel, ref?} */
export const LinkTargetInput = ({ siteId, value, onChange }) => {
    const { t } = useTranslation();
    const v = value && typeof value === 'object' ? value : { href: '', label: '' };
    const set = (patch) => {
        const next = { ...v, ...patch };
        if (!next.ref) delete next.ref;
        onChange(next);
    };
    return (
        <div style={{ display: 'grid', gap: 4 }}>
            <Input size="small" placeholder={t('Label')} value={v.label} onChange={(e) => set({ label: e.target.value })} />
            {siteId ? (
                <ObjectSearch siteId={siteId} allowUrl allowClear needUrl value={v.ref} href={v.href}
                    onPick={(it) => set({ href: it.url, ref: { kind: it.kind, id: it.id }, label: v.label || it.title })}
                    onUrl={(href) => set({ href, ref: null })}
                    onClear={() => set({ href: '', ref: null })} />
            ) : (
                <Input size="small" placeholder={t('URL or page path')} value={v.href} onChange={(e) => set({ href: e.target.value, ref: null })} />
            )}
            <Checkbox checked={v.target === '_blank'} onChange={(e) => set({ target: e.target.checked ? '_blank' : '' })}>
                <Text style={{ fontSize: 12 }}>{t('Open in a new tab')}</Text>
            </Checkbox>
        </div>
    );
};

/** A plain URL string (menus, redirects): pages / products / posts / collections or anything typed. */
export const UrlPicker = ({ siteId, value, onChange, placeholder, size = 'middle', style }) => (
    <ObjectSearch siteId={siteId} kinds={['page', 'product', 'post', 'collection', 'item']} allowUrl allowClear needUrl size={size} style={style}
        href={value || ''} placeholder={placeholder}
        onPick={(it) => onChange(it.url, it)} onUrl={(u) => onChange(u)} onClear={() => onChange('')} />
);

/** ref value {kind, id} */
export const RefInput = ({ siteId, f, value, onChange }) => {
    const kinds = (Array.isArray(f.kinds) && f.kinds.length ? f.kinds : REF_KINDS).filter((k) => REF_KINDS.includes(k));
    const v = value && typeof value === 'object' && value.kind && value.id ? value : null;
    if (!siteId) return <Text type="secondary" style={{ fontSize: 12 }}>{v ? `${v.kind}: ${v.id}` : '—'}</Text>;
    return <ObjectSearch siteId={siteId} kinds={kinds} value={v} allowClear onPick={(it) => onChange({ kind: it.kind, id: it.id })} onClear={() => onChange(null)} />;
};

// ---------- source ----------
const useValues = (siteId, vkind) => {
    const [state, setState] = useState({ kind: null, items: [], loading: false });
    useEffect(() => {
        if (!siteId || !vkind) return undefined;
        let alive = true;
        setState((s) => ({ ...s, loading: true }));
        siteApi(siteId).get('/picker/values', { kind: vkind }, QUIET)
            .then((r) => { if (alive) setState({ kind: vkind, items: r?.items || [], loading: false }); })
            .catch(() => { if (alive) setState({ kind: vkind, items: [], loading: false }); });
        return () => { alive = false; };
    }, [siteId, vkind]);
    return state.kind === vkind ? state : { items: [], loading: !!vkind };
};

const ManualList = ({ siteId, itemKind, ids, onChange, max }) => {
    const { t } = useTranslation();
    const refs = useMemo(() => ids.map((id) => ({ kind: itemKind, id })), [itemKind, ids]);
    const items = useRefItems(siteId, refs);
    const dragFrom = useRef(null);
    const move = (from, to) => {
        if (from === to || to < 0 || to >= ids.length) return;
        const next = [...ids];
        const [x] = next.splice(from, 1);
        next.splice(to, 0, x);
        onChange(next);
    };
    return (
        <div>
            {ids.map((id, i) => {
                const it = items[`${itemKind}|${id}`];
                return (
                    <div key={id} draggable
                        onDragStart={(e) => { dragFrom.current = i; e.dataTransfer.effectAllowed = 'move'; e.stopPropagation(); }}
                        onDragOver={(e) => { if (dragFrom.current !== null) e.preventDefault(); }}
                        onDrop={(e) => { e.preventDefault(); if (dragFrom.current !== null) move(dragFrom.current, i); dragFrom.current = null; }}
                        style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 4px', marginBottom: 4, border: '1px solid #e5e7eb', borderRadius: 6, background: '#fff', fontSize: 12 }}>
                        <HolderOutlined style={{ cursor: 'grab', color: '#9ca3af' }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {it ? <ItemRow it={it} /> : <span>{it === false ? <Text type="danger">{t('Not found')}: </Text> : null}{id}</span>}
                        </div>
                        <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => onChange(ids.filter((x) => x !== id))} />
                    </div>
                );
            })}
            {ids.length < max && (
                <ObjectSearch siteId={siteId} kinds={[itemKind]} value={null} exclude={ids.map((id) => `${itemKind}:${id}`)}
                    placeholder={t('Add…')} onPick={(it) => onChange([...ids, it.id])} />
            )}
        </div>
    );
};

const row = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 12 };

/** source value {kind, from, value, ids, sort, limit, exclude_current} */
export const SourceInput = ({ siteId, f, value, onChange }) => {
    const { t } = useTranslation();
    const L = useKindLabels();
    const [more, setMore] = useState(false);
    const kinds = (Array.isArray(f.kinds) && f.kinds.length ? f.kinds : SOURCE_KINDS).filter((k) => SOURCE_KINDS.includes(k));
    const max = Math.min(Number(f.max) > 0 ? Number(f.max) : 24, 100); // SPEC §1.1a: limit ≤ field max (hard cap 100), manual ids ≤ 48
    const base = { ...(f.default && typeof f.default === 'object' ? f.default : {}), ...(value && typeof value === 'object' ? value : {}) };
    const kind = kinds.includes(base.kind) ? base.kind : kinds[0];
    const froms = FROMS[kind] || [];
    const from = froms.includes(base.from) ? base.from : froms[0];
    const v = { ...base, kind, from, limit: Math.min(Math.max(Number(base.limit) || Math.min(8, max), 1), max) };
    const ids = Array.isArray(v.ids) ? v.ids.filter((x) => typeof x === 'string') : [];
    const set = (patch) => {
        const next = { ...v, ...patch };
        Object.keys(next).forEach((k) => { if (next[k] === undefined || next[k] === '' || (k === 'ids' && !next[k]?.length)) delete next[k]; });
        if (next.from !== 'manual') delete next.ids;
        if (!VALUE_KIND[`${next.kind}.${next.from}`]) delete next.value;
        if (next.sort === 'default') delete next.sort;
        if (!next.exclude_current) delete next.exclude_current;
        onChange(next);
    };
    const vkind = VALUE_KIND[`${kind}.${from}`];
    const vals = useValues(siteId, vkind);
    const manual = from === 'manual';
    return (
        <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 8, background: '#fafbfc' }}>
            {kinds.length > 1 && (
                <Segmented size="small" block style={{ marginBottom: 6 }} value={kind}
                    options={kinds.map((k) => ({ value: k, label: L[k] }))}
                    onChange={(k) => set({ kind: k, from: FROMS[k][0], value: undefined, ids: undefined, sort: undefined })} />
            )}
            <Select size="small" style={{ width: '100%', marginBottom: 6 }} value={from}
                options={froms.map((x) => ({ value: x, label: L.from[x] }))}
                onChange={(x) => set({ from: x, value: undefined, ids: x === 'manual' ? ids : undefined })} />
            {vkind && (
                <Select size="small" style={{ width: '100%', marginBottom: 6 }} showSearch allowClear value={v.value || undefined}
                    loading={vals.loading} placeholder={L.from[from]} optionFilterProp="search"
                    options={[...vals.items, ...(v.value && !vals.items.some((x) => x.value === v.value) ? [{ value: v.value, label: v.value }] : [])]
                        .map((x) => ({ value: x.value, search: `${x.label} ${x.value}`, label: x.count != null ? <span>{x.label} <Text type="secondary" style={{ fontSize: 11 }}>{x.count}</Text></span> : x.label }))}
                    notFoundContent={<Text type="secondary" style={{ fontSize: 12 }}>{t('No results')}</Text>}
                    onChange={(x) => set({ value: x })} />
            )}
            {manual && (siteId
                ? <ManualList siteId={siteId} itemKind={ITEM_KIND[kind]} ids={ids} max={Math.min(max, 48)}
                    onChange={(next) => set({ ids: next, limit: Math.min(Math.max(next.length, 1), max) })} />
                : <Text type="secondary" style={{ fontSize: 12 }}>{ids.join(', ') || '—'}</Text>)}
            {!manual && (
                <div style={row}>
                    <span style={{ flex: 'none' }}>{t('Show')}</span>
                    <Slider style={{ flex: 1, margin: '0 6px' }} min={1} max={max} value={v.limit} onChange={(n) => set({ limit: n })} />
                    <span style={{ width: 22, textAlign: 'right' }}>{v.limit}</span>
                </div>
            )}
            <div>
                <Button size="small" type="link" style={{ padding: 0, fontSize: 12 }} icon={more ? <CaretDownOutlined /> : <CaretRightOutlined />} onClick={() => setMore(!more)}>
                    {t('More options')}
                </Button>
            </div>
            {more && (
                <div style={{ marginTop: 6 }}>
                    {!manual && (
                        <div style={row}>
                            <span style={{ flex: 1 }}>{t('Sort')}</span>
                            <Select size="small" style={{ width: 150 }} value={v.sort || 'default'}
                                options={(SORTS[kind] || []).map((s) => ({ value: s, label: L.sort[s] }))} onChange={(s) => set({ sort: s })} />
                        </div>
                    )}
                    <div style={row}>
                        <span style={{ flex: 1 }}>
                            {t('Hide the current item')}{' '}
                            <Tooltip title={t('On a product or post page, leave that product or post out (related items)')}><Text type="secondary">?</Text></Tooltip>
                        </span>
                        <Switch size="small" checked={!!v.exclude_current} onChange={(x) => set({ exclude_current: x })} />
                    </div>
                </div>
            )}
        </div>
    );
};
